import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkDetailPage from '../../../components/work/WorkDetailPage';
import MainLayout from '../../../layouts/MainLayout';
import WorkFilePanel from '../../../components/work/shared/WorkFilePanel';
import WorkCommentPanel from '../../../components/work/shared/WorkCommentPanel';
import SubTaskAccordion from '../../../components/work/tasks/SubTaskAccordion';
import ChaproomPostalInfoBox from '../../../components/work/tasks/ChaproomPostalInfoBox';
import ChaproomPackagingShippingModal from '../../../components/work/tasks/ChaproomPackagingShippingModal';
import InlineEditActions from '../../../components/common/InlineEditActions';
import {
  tasksApi,
  taskFilesApi,
  taskCommentsApi,
  taskNotesApi,
  subTasksApi,
} from '../../../utils/workApi';
import {
  buildSubTaskPayload,
  buildTaskCommentPayload,
  buildTaskNotePayload,
} from '../../../utils/workPayloadBuilders';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { formatJalaliApiDate, getTodayJalali, toJalali } from '../../../utils/dateUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useClickOutsideWhen } from '../../../hooks/useClickOutsideWhen';
import {
  Button,
  Card,
  Tag,
  Space,
  message,
  Tabs,
  List,
  Input,
  Select,
  Popconfirm,
  Modal,
  Form,
  Avatar,
  Tooltip,
  Empty,
  Badge,
  Timeline,
  Table,
} from 'antd';
import {
  EditOutlined,
  PushpinOutlined,
  PushpinFilled,
  DownloadOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  ProjectOutlined,
  UserOutlined,
  FlagOutlined,
  PaperClipOutlined,
  CommentOutlined,
  FormOutlined,
  DeleteOutlined,
  FieldTimeOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import PermissionGate from '../../../components/PermissionGate';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import {
  diffNewFiles,
  extractChaproomOrderId,
  hasCompanyFeature,
  isChaproomOrderCanceled,
  isChaproomPackagingColumn,
  isChaproomShippingExtra,
} from '../../../company/features';

const priorityFa = {
  high: { text: 'بالا', color: 'red' },
  medium: { text: 'متوسط', color: 'blue' },
  low: { text: 'پایین', color: 'default' },
};

const HISTORY_LABELS = {
  createActivity: 'ایجاد وظیفه',
  statusActivity: 'تغییر وضعیت',
  fileActivity: 'فعالیت فایل',
  subTaskCreateActivity: 'ایجاد زیروظیفه',
  subTaskUpdateActivity: 'به‌روزرسانی زیروظیفه',
  subTaskDeleteActivity: 'حذف زیروظیفه',
  commentActivity: 'نظر',
  noteActivity: 'یادداشت',
  updateActivity: 'به‌روزرسانی وظیفه',
};

/** ترجیح ISO (due_date/start_date) نسبت به due_on مبهم */
const fmtDate = (v) => (v ? toJalali(v) || v : '—');
const fmtTaskDue = (task) => fmtDate(task?.due_date || task?.due_on);
const fmtTaskStart = (task) => fmtDate(task?.start_date || task?.start_on);

const fmtDateTime = (v) => {
  if (!v) return null;
  return toJalali(v, 'jYYYY/jMM/jDD HH:mm') || toJalali(v) || v;
};

const resolveNoteAuthor = (n) =>
  n?.user?.name ||
  n?.added_by?.name ||
  n?.creator?.name ||
  n?.created_by?.name ||
  (typeof n?.created_by === 'string' ? n.created_by : null) ||
  n?.user_name ||
  '—';

const resolveNoteTime = (n) =>
  n?.created_at || n?.created_on || n?.updated_at || n?.date || null;

const historyTitle = (item) => {
  const base = HISTORY_LABELS[item?.details] || item?.details || 'فعالیت';
  if (item?.sub_task?.title) return `${base}: ${item.sub_task.title}`;
  if (item?.board_column?.column_name) return `${base} → ${item.board_column.column_name}`;
  return base;
};

const MetaItem = ({ icon, label, children, onEdit, className = '' }) => (
  <div
    className={`rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-3 h-full ${
      onEdit ? 'cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 transition-colors' : ''
    } ${className}`}
    onClick={onEdit}
    role={onEdit ? 'button' : undefined}
    tabIndex={onEdit ? 0 : undefined}
    onKeyDown={
      onEdit
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit();
            }
          }
        : undefined
    }
  >
    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
      {icon}
      <span>{label}</span>
      {onEdit ? <EditOutlined className="text-[10px] opacity-50 mr-auto" /> : null}
    </div>
    <div className="text-sm text-gray-800 dark:text-gray-100 font-medium" style={{ fontFamily: 'CustomFont' }}>
      {children}
    </div>
  </div>
);

const ViewTask = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBack = () =>
    navigate(returnToRef.current || '/work/tasks', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/tasks',
    listLabel: 'مدیریت وظایف',
    entityLabel: task?.heading || (id ? `وظیفه #${id}` : 'وظیفه'),
  });
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteSaving, setEditNoteSaving] = useState(false);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalLoading, setSubModalLoading] = useState(false);
  const [subSaving, setSubSaving] = useState(false);
  const [subEmployees, setSubEmployees] = useState([]);
  const [subForm] = Form.useForm();
  const [statusCol, setStatusCol] = useState(null);
  const [columns, setColumns] = useState([]);
  const [statusSaving, setStatusSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('files');
  const defaultTabAppliedRef = useRef(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState(null);
  const [updatingField, setUpdatingField] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [chaproomStatusLoading, setChaproomStatusLoading] = useState(false);
  const [chaproomCanceled, setChaproomCanceled] = useState(false);
  const [packagingModalOpen, setPackagingModalOpen] = useState(false);
  const [packagingSaving, setPackagingSaving] = useState(false);
  const [pendingPackaging, setPendingPackaging] = useState(null);
  const editingBoxRef = useRef(null);

  const cancelEditing = useCallback(() => {
    setEditingField(null);
    setFieldValue(null);
  }, []);

  useClickOutsideWhen(Boolean(editingField), cancelEditing, editingBoxRef);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await tasksApi.getById(id);
      const t = res?.task || res?.data?.task || null;
      setTask(t);
      setStatusCol(t?.board_column_id || t?.board_column?.id);
      const empList =
        res?.employees ||
        res?.data?.employees ||
        t?.employees ||
        [];
      if (Array.isArray(empList) && empList.length) {
        setEmployees(empList);
      }
      if (!silent && !defaultTabAppliedRef.current) {
        const subs = t?.subtasks || t?.sub_tasks || [];
        setActiveTab(Array.isArray(subs) && subs.length > 0 ? 'subtasks' : 'files');
        defaultTabAppliedRef.current = true;
      }
      if (!silent) {
        const colsFromTask =
          res?.taskboard_columns ||
          res?.boardColumns ||
          res?.data?.taskboard_columns ||
          [];
        if (Array.isArray(colsFromTask) && colsFromTask.length) {
          setColumns(colsFromTask);
        } else {
          const board = await tasksApi.getBoard({ project_id: t?.project_id || 'all' });
          setColumns(board?.boardColumns || board?.taskboard_columns || board?.columns || []);
        }
        if (!Array.isArray(empList) || !empList.length) {
          try {
            const createRes = await tasksApi.getCreateData();
            setEmployees(createRes?.employees || createRes?.data?.employees || t?.users || []);
          } catch {
            setEmployees(t?.users || []);
          }
        }
      }
    } catch {
      message.error('خطا در دریافت وظیفه');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    defaultTabAppliedRef.current = false;
    setChaproomCanceled(false);
    load();
  }, [load]);

  const refreshTask = useCallback(() => load({ silent: true }), [load]);

  const handleChaproomStatusCheck = async () => {
    const orderId = extractChaproomOrderId(task);
    if (!orderId) {
      message.warning('شماره سفارش در اطلاعات وظیفه یافت نشد');
      return;
    }
    setChaproomStatusLoading(true);
    try {
      const res = await tasksApi.getChaproomTaskStatus(orderId);
      const msg = res?.message || res?.data?.message || 'وضعیت سفارش دریافت شد';
      const canceled = isChaproomOrderCanceled(res);
      setChaproomCanceled(canceled);
      if (canceled) {
        message.warning(msg);
      } else {
        message.success(msg);
      }
    } catch (error) {
      setChaproomCanceled(false);
      message.error(error?.message || 'خطا در بررسی وضعیت سفارش');
    } finally {
      setChaproomStatusLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    await tasksApi.delete(id);
    message.success('حذف شد');
    navigate(returnToRef.current || '/work/tasks', { state: buildGoBackState() });
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setFieldValue(currentValue);
  };

  const handleUpdateField = async (field, value) => {
    let payloadValue = value;
    if (field === 'due_date' || field === 'start_date') {
      payloadValue = formatJalaliApiDate(value);
      if (!payloadValue) {
        message.warning('تاریخ معتبر نیست');
        return;
      }
    }
    if (field === 'user_id') {
      payloadValue = value != null && value !== '' ? String(value) : '';
    }
    if (field === 'heading' && !String(payloadValue || '').trim()) {
      message.warning('عنوان الزامی است');
      return;
    }

    setUpdatingField(true);
    try {
      await tasksApi.updateSingleField(id, field, payloadValue);
      message.success('با موفقیت به‌روز شد');
      cancelEditing();
      await refreshTask();
    } catch {
      message.error('خطا در به‌روزرسانی');
    } finally {
      setUpdatingField(false);
    }
  };

  const changeStatus = async (colId, shippingExtra = null) => {
    const nextId = colId ?? statusCol;
    const col = columns.find((c) => String(c.id) === String(nextId));
    const status = col?.slug || nextId;
    if (status == null || status === '') return false;

    const needsShippingModal =
      !isChaproomShippingExtra(shippingExtra) &&
      hasCompanyFeature(user?.company_id ?? task?.company_id, 'task.chaproomPackagingShipping') &&
      isChaproomPackagingColumn(col);

    if (needsShippingModal) {
      setPendingPackaging({ colId: nextId, status, previousColId: statusCol });
      setPackagingModalOpen(true);
      return false;
    }

    setStatusSaving(true);
    try {
      setStatusCol(nextId);
      await tasksApi.changeStatus(
        id,
        status,
        isChaproomShippingExtra(shippingExtra) ? shippingExtra : {},
      );
      message.success('وضعیت به‌روز شد');
      refreshTask();
      return true;
    } catch {
      message.error('خطا در تغییر وضعیت');
      setStatusCol(task?.board_column_id || task?.board_column?.id || pendingPackaging?.previousColId);
      return false;
    } finally {
      setStatusSaving(false);
    }
  };

  const handlePackagingModalSubmit = async (values) => {
    if (!pendingPackaging) return;
    setPackagingSaving(true);
    try {
      const ok = await changeStatus(pendingPackaging.colId, {
        trackingCode: values.trackingCode,
        postageMethod: values.postageMethod,
      });
      if (ok) {
        setPackagingModalOpen(false);
        setPendingPackaging(null);
      }
    } finally {
      setPackagingSaving(false);
    }
  };

  const handlePackagingModalCancel = () => {
    if (packagingSaving) return;
    setPackagingModalOpen(false);
    if (pendingPackaging?.previousColId != null) {
      setStatusCol(pendingPackaging.previousColId);
    } else {
      setStatusCol(task?.board_column_id || task?.board_column?.id);
    }
    setPendingPackaging(null);
  };

  const togglePin = async () => {
    try {
      if (task?.pinned) {
        await tasksApi.destroyPin(task.pinned);
      } else {
        await tasksApi.storePin(id, task?.project_id || task?.project?.id);
      }
      message.success('سنجاق به‌روز شد');
      refreshTask();
    } catch {
      message.error('خطا در سنجاق');
    }
  };

  const openSubModal = async () => {
    setSubModalLoading(true);
    try {
      const res = await subTasksApi.getCreateData(id);
      const fromCreate = res?.employees || res?.data?.employees || [];
      const fallback = task?.users || [];
      setSubEmployees(fromCreate.length ? fromCreate : fallback);
      subForm.resetFields();
      subForm.setFieldsValue({
        start_date: getTodayJalali('jYYYY/jMM/jDD'),
      });
      setSubModalOpen(true);
    } catch {
      const fallback = task?.users || [];
      if (fallback.length) {
        setSubEmployees(fallback);
        subForm.resetFields();
        subForm.setFieldsValue({ start_date: getTodayJalali('jYYYY/jMM/jDD') });
        setSubModalOpen(true);
      } else {
        message.error('خطا در بارگذاری فرم زیروظیفه');
      }
    } finally {
      setSubModalLoading(false);
    }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      setActiveTab('notes');
      await taskNotesApi.create(buildTaskNotePayload(id, noteText));
      setNoteText('');
      message.success('یادداشت ثبت شد');
      await refreshTask();
      setActiveTab('notes');
    } catch {
      message.error('خطا در ثبت یادداشت');
    } finally {
      setNoteSaving(false);
    }
  };

  const saveEditNote = async () => {
    if (!editingNote?.id || !editNoteText.trim()) return;
    setEditNoteSaving(true);
    try {
      setActiveTab('notes');
      await taskNotesApi.update(editingNote.id, {
        ...buildTaskNotePayload(id, editNoteText.trim()),
      });
      message.success('یادداشت ویرایش شد');
      setEditingNote(null);
      setEditNoteText('');
      await refreshTask();
      setActiveTab('notes');
    } catch {
      message.error('خطا در ویرایش یادداشت');
    } finally {
      setEditNoteSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <TableSkeleton rows={6} columns={2} />
      </MainLayout>
    );
  }

  if (!task) {
    return (
      <MainLayout>
        <Card>وظیفه یافت نشد</Card>
      </MainLayout>
    );
  }

  const files = task.files || [];
  const comments = task.comments || [];
  const notes = task.notes || [];
  const subtasks = task.subtasks || task.sub_tasks || [];
  const users = task.users || [];
  const labels = task.labels || task.task_labels || task.label || [];
  const history = task.history || [];
  const timeLogs = [
    ...(Array.isArray(task.approved_time_logs) ? task.approved_time_logs : []),
    ...(Array.isArray(task.time_logged) ? task.time_logged : []),
    ...(Array.isArray(task.time_logs) ? task.time_logs : []),
  ];
  const pri = priorityFa[task.priority] || { text: task.priority || '—', color: 'default' };
  const doneCount = subtasks.filter((s) => s.status === 'complete' || s.status === 'completed').length;
  const employeeOptions = (employees.length ? employees : users).map((e) => ({
    value: e.id,
    label: e.name,
  }));
  const primaryUserId = users[0]?.id;

  const timeLogColumns = [
    {
      title: 'کاربر',
      key: 'user',
      render: (_, row) => row.user?.name || row.employee?.name || row.name || '—',
    },
    {
      title: 'شروع',
      key: 'start',
      render: (_, row) => fmtDateTime(row.start_time || row.start_date || row.created_at) || '—',
    },
    {
      title: 'پایان',
      key: 'end',
      render: (_, row) => fmtDateTime(row.end_time || row.end_date) || '—',
    },
    {
      title: 'مدت',
      key: 'hours',
      render: (_, row) =>
        row.total_hours ||
        row.hours ||
        (row.hours != null || row.minutes != null
          ? `${row.hours || 0}س ${row.minutes || 0}د`
          : '—'),
    },
    {
      title: 'توضیح',
      dataIndex: 'memo',
      ellipsis: true,
      render: (v, row) => v || row.note || row.reason || '—',
    },
  ];

  const tabItems = [
    {
      key: 'files',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <PaperClipOutlined className="ml-1" />
          فایل‌ها
          {files.length ? ` (${files.length})` : ''}
        </span>
      ),
      children: (
        <div>
          <WorkFilePanel
            files={files}
            onUpload={async (fl, opts) => {
              const beforeFiles = Array.isArray(files) ? [...files] : [];
              const res = await taskFilesApi.upload(id, fl, opts);
              const loadAfterFiles = async () => {
                const fresh = await tasksApi.getById(id);
                const t = fresh?.task || fresh?.data?.task || null;
                const after = t?.files || [];
                if (t) setTask(t);
                return after;
              };
              let afterFiles = [];
              try {
                afterFiles = await loadAfterFiles();
                // گاهی بلافاصله بعد از chunk هنوز فایل در GET نیست
                if (opts?.needConvert && !diffNewFiles(beforeFiles, afterFiles, fl.length).length) {
                  await new Promise((r) => setTimeout(r, 800));
                  afterFiles = await loadAfterFiles();
                }
              } catch {
                await refreshTask();
              }
              const newFiles = diffNewFiles(beforeFiles, afterFiles, fl.length);
              return {
                ...(res && typeof res === 'object' ? res : {}),
                __newFiles: newFiles,
                __afterFiles: afterFiles,
                files: newFiles.length ? newFiles : res?.files || [],
              };
            }}
            onDelete={(fid) => taskFilesApi.delete(fid).then(refreshTask)}
            getDownloadUrl={taskFilesApi.downloadUrl}
            askNeedConvert={hasCompanyFeature(user?.company_id, 'task.fileNeedConvertPrompt')}
            getConvertProgress={(fileId) => taskFilesApi.getConvertProgress(fileId)}
            onConvertComplete={refreshTask}
          />
          {files.length > 0 ? (
            <Space className="mt-4" wrap>
              <Button
                danger
                onClick={async () => {
                  await taskFilesApi.deleteAll(id);
                  refreshTask();
                }}
              >
                حذف همه فایل‌ها
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={async () => {
                  try {
                    await taskFilesApi.downloadAll(id);
                    message.success('دانلود همه فایل‌ها شروع شد');
                  } catch {
                    message.error('خطا در دانلود فایل‌ها');
                  }
                }}
              >
                دانلود همه
              </Button>
            </Space>
          ) : null}
        </div>
      ),
    },
    {
      key: 'subtasks',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <CheckSquareOutlined className="ml-1" />
          زیروظیفه
          {subtasks.length ? ` (${doneCount}/${subtasks.length})` : ''}
        </span>
      ),
      children: (
        <SubTaskAccordion
          subtasks={subtasks}
          taskId={id}
          askNeedConvert={hasCompanyFeature(user?.company_id, 'task.fileNeedConvertPrompt')}
          onRefresh={async () => {
            setActiveTab('subtasks');
            await refreshTask();
            setActiveTab('subtasks');
          }}
          onAddClick={openSubModal}
          addLoading={subModalLoading}
        />
      ),
    },
    {
      key: 'comments',
      label: (
        <span style={{ fontFamily: 'CustomFont' }} className="inline-flex items-center gap-1">
          <CommentOutlined className="ml-1" />
          نظرات
          {comments.length ? (
            <Badge
              count={comments.length}
              size="small"
              style={{ backgroundColor: '#7c3aed', fontFamily: 'CustomFont' }}
            />
          ) : null}
        </span>
      ),
      children: (
        <WorkCommentPanel
          comments={comments}
          currentUserId={user?.id}
          onCreate={async (text) => {
            setActiveTab('comments');
            await taskCommentsApi.create(buildTaskCommentPayload(id, text));
            await refreshTask();
            setActiveTab('comments');
          }}
          onUpdate={async (cid, text) => {
            setActiveTab('comments');
            await taskCommentsApi.update(cid, { comment: text });
            await refreshTask();
            setActiveTab('comments');
          }}
          onDelete={async (cid) => {
            setActiveTab('comments');
            await taskCommentsApi.delete(cid);
            await refreshTask();
            setActiveTab('comments');
          }}
          onLike={async (commentId, emojiName) => {
            setActiveTab('comments');
            await taskCommentsApi.saveLike(commentId, emojiName);
            await refreshTask();
            setActiveTab('comments');
          }}
        />
      ),
    },
    {
      key: 'time-logs',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <FieldTimeOutlined className="ml-1" />
          برگه زمان
          {timeLogs.length ? ` (${timeLogs.length})` : ''}
        </span>
      ),
      children: (
        <div style={{ fontFamily: 'CustomFont' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <MetaItem icon={<FieldTimeOutlined />} label="برآورد زمان">
              {task.estimate_hours != null || task.estimate_minutes != null
                ? `${task.estimate_hours || 0} ساعت و ${task.estimate_minutes || 0} دقیقه`
                : '—'}
            </MetaItem>
            <MetaItem icon={<FieldTimeOutlined />} label="تایمر فعال">
              {task.user_active_timer
                ? (task.user_active_timer.user?.name || 'در حال ثبت')
                : 'ندارد'}
            </MetaItem>
            <MetaItem icon={<FieldTimeOutlined />} label="تعداد رکورد">
              {timeLogs.length}
            </MetaItem>
          </div>
          {timeLogs.length === 0 ? (
            <Empty description={<span style={{ fontFamily: 'CustomFont' }}>برگه زمانی ثبت نشده</span>} />
          ) : (
            <Table
              rowKey={(r) => r.id || `${r.start_time}-${r.user_id}`}
              dataSource={timeLogs}
              columns={timeLogColumns}
              pagination={false}
              size="small"
              scroll={{ x: true }}
            />
          )}
        </div>
      ),
    },
    {
      key: 'notes',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <FormOutlined className="ml-1" />
          یادداشت‌ها
          {notes.length ? ` (${notes.length})` : ''}
        </span>
      ),
      children: (
        <div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input.TextArea
              rows={2}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="یادداشت جدید..."
              style={{ fontFamily: 'CustomFont' }}
            />
            <Button
              type="primary"
              className={PANEL_PRIMARY_BTN_CLASS}
              loading={noteSaving}
              onClick={saveNote}
            >
              افزودن
            </Button>
          </div>
          {notes.length === 0 ? (
            <Empty description={<span style={{ fontFamily: 'CustomFont' }}>یادداشتی نیست</span>} />
          ) : (
            <List
              dataSource={notes}
              renderItem={(n) => {
                const author = resolveNoteAuthor(n);
                const when = fmtDateTime(resolveNoteTime(n));
                return (
                  <List.Item
                    className="!px-3 rounded-lg border border-gray-100 dark:border-gray-700 mb-2"
                    actions={[
                      <Button
                        key="e"
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveTab('notes');
                          setEditingNote(n);
                          setEditNoteText(n.note || '');
                        }}
                      >
                        ویرایش
                      </Button>,
                      <Popconfirm
                        key="d"
                        title="حذف یادداشت؟"
                        onConfirm={async () => {
                          setActiveTab('notes');
                          await taskNotesApi.delete(n.id);
                          await refreshTask();
                          setActiveTab('notes');
                        }}
                      >
                        <Button type="link" danger size="small">
                          حذف
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <div className="w-full" style={{ fontFamily: 'CustomFont' }}>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                        <Avatar size={22} src={n.user?.image_url || n.user?.image || n.added_by?.image_url}>
                          {author?.[0]}
                        </Avatar>
                        <span className="font-medium text-gray-700 dark:text-gray-200">{author}</span>
                        {when ? (
                          <>
                            <span>·</span>
                            <span>{when}</span>
                          </>
                        ) : null}
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
                        {n.note}
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      ),
    },
    {
      key: 'history',
      label: (
        <span style={{ fontFamily: 'CustomFont' }}>
          <HistoryOutlined className="ml-1" />
          تاریخچه
          {history.length ? ` (${history.length})` : ''}
        </span>
      ),
      children: (
        <div style={{ fontFamily: 'CustomFont' }}>
          {history.length === 0 ? (
            <Empty description={<span style={{ fontFamily: 'CustomFont' }}>تاریخچه‌ای ثبت نشده</span>} />
          ) : (
            <Timeline
              items={history.map((h) => ({
                color: h.board_column?.label_color || 'blue',
                children: (
                  <div className="pb-2">
                    <div className="font-medium text-gray-800 dark:text-gray-100">
                      {historyTitle(h)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                      <Avatar size={20} src={h.user?.image_url}>
                        {h.user?.name?.[0]}
                      </Avatar>
                      <span>{h.user?.name || '—'}</span>
                      {h.board_column?.column_name ? (
                        <Tag color={h.board_column.label_color} className="m-0 text-[10px]">
                          {h.board_column.column_name}
                        </Tag>
                      ) : null}
                      {fmtDateTime(h.created_at || h.updated_at) ? (
                        <span>{fmtDateTime(h.created_at || h.updated_at)}</span>
                      ) : null}
                    </div>
                  </div>
                ),
              }))}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <WorkDetailPage
      title={task.heading}
      subtitle={task.task_short_code}
      icon={CheckSquareOutlined}
      backPath="/work/tasks"
      onBack={goBack}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
      headerExtra={(
        <Space wrap>
          <Button
            icon={task.pinned ? <PushpinFilled className="text-amber-500" /> : <PushpinOutlined />}
            onClick={togglePin}
            style={{ fontFamily: 'CustomFont' }}
          >
            {task.pinned ? 'برداشتن سنجاق' : 'سنجاق'}
          </Button>
          <PermissionGate permission="edit_task">
            <Button
              type="primary"
              icon={<EditOutlined />}
              className={PANEL_PRIMARY_BTN_CLASS}
              onClick={() =>
                navigateWithTrail(
                  `/work/tasks/${id}/edit`,
                  task?.heading ? `ویرایش ${task.heading}` : `ویرایش وظیفه #${id}`,
                  {
                    returnTo: returnToRef.current || '/work/tasks',
                    ...buildGoBackState(),
                  },
                )
              }
              style={{ fontFamily: 'CustomFont' }}
            >
              ویرایش
            </Button>
          </PermissionGate>
          <PermissionGate permission="delete_task">
            <Popconfirm title="حذف وظیفه؟" onConfirm={handleDeleteTask}>
              <Button danger icon={<DeleteOutlined />} style={{ fontFamily: 'CustomFont' }}>
                حذف
              </Button>
            </Popconfirm>
          </PermissionGate>
        </Space>
      )}
    >
      <div className="space-y-4" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-sm border-0 overflow-hidden" bodyStyle={{ padding: 0 }}>
          <div className="bg-gradient-to-l from-violet-50 via-white to-indigo-50 dark:from-violet-950/30 dark:via-gray-800 dark:to-indigo-950/20 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex flex-wrap items-center gap-2">
                <Tag color={task.board_column?.label_color || 'purple'} className="m-0">
                  {task.board_column?.column_name || task.status || 'بدون وضعیت'}
                </Tag>
                {editingField === 'priority' ? (
                  <div
                    ref={editingBoxRef}
                    className="inline-flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Select
                      value={fieldValue}
                      onChange={setFieldValue}
                      style={{ minWidth: 120, fontFamily: 'CustomFont' }}
                      options={[
                        { value: 'high', label: 'بالا' },
                        { value: 'medium', label: 'متوسط' },
                        { value: 'low', label: 'پایین' },
                      ]}
                    />
                    <InlineEditActions
                      onSave={() => handleUpdateField('priority', fieldValue)}
                      onCancel={cancelEditing}
                      saving={updatingField}
                    />
                  </div>
                ) : (
                  <Tag
                    color={pri.color}
                    className="m-0 cursor-pointer hover:opacity-80"
                    onClick={() => startEditing('priority', task.priority || 'medium')}
                  >
                    اولویت: {pri.text} <EditOutlined className="text-[10px] mr-1 opacity-60" />
                  </Tag>
                )}
                {labels.map((l) => (
                  <Tag key={l.id || l.label_id} color={l.label_color || l.color} className="m-0">
                    {l.label_name || l.name || `تگ #${l.label_id || l.id}`}
                  </Tag>
                ))}
              </div>
              {hasCompanyFeature(user?.company_id ?? task?.company_id, 'task.chaproomStatusCheck') ? (
                <Space wrap size="small">
                  <Button
                    type="primary"
                    loading={chaproomStatusLoading}
                    onClick={handleChaproomStatusCheck}
                    className={PANEL_PRIMARY_BTN_CLASS}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    بررسی وضعیت
                  </Button>
                  {chaproomCanceled ? (
                    <PermissionGate permission="delete_task">
                      <Popconfirm title="حذف وظیفه؟" onConfirm={handleDeleteTask}>
                        <Button danger icon={<DeleteOutlined />} style={{ fontFamily: 'CustomFont' }}>
                          حذف وظیفه
                        </Button>
                      </Popconfirm>
                    </PermissionGate>
                  ) : null}
                </Space>
              ) : null}
            </div>
            {editingField === 'heading' ? (
              <div ref={editingBoxRef} className="flex items-start gap-2 mb-3">
                <Input
                  value={fieldValue || ''}
                  onChange={(e) => setFieldValue(e.target.value)}
                  onPressEnter={() => handleUpdateField('heading', fieldValue)}
                  className="text-lg font-bold"
                  style={{ fontFamily: 'CustomFont' }}
                  autoFocus
                />
                <InlineEditActions
                  onSave={() => handleUpdateField('heading', fieldValue)}
                  onCancel={cancelEditing}
                  saving={updatingField}
                />
              </div>
            ) : (
              <h2
                className="text-xl font-bold text-gray-900 dark:text-gray-50 m-0 mb-3 leading-relaxed cursor-pointer hover:text-indigo-700 dark:hover:text-indigo-300 group inline-flex items-start gap-2"
                onClick={() => startEditing('heading', task.heading || '')}
                title="ویرایش عنوان"
              >
                <span>{task.heading}</span>
                <EditOutlined className="text-sm opacity-0 group-hover:opacity-50 mt-1.5 shrink-0" />
              </h2>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={statusCol}
                onChange={(colId) => changeStatus(colId)}
                loading={statusSaving}
                style={{ minWidth: 180, fontFamily: 'CustomFont' }}
                options={columns.map((c) => ({ value: c.id, label: c.column_name }))}
                placeholder="تغییر وضعیت"
              />
              <Avatar.Group maxCount={5} size="default">
                {users.map((u) => (
                  <Tooltip key={u.id} title={u.name}>
                    <Avatar src={u.image_url || u.image}>{u.name?.[0]}</Avatar>
                  </Tooltip>
                ))}
              </Avatar.Group>
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetaItem icon={<ProjectOutlined />} label="پروژه">
              {task.project?.project_name || task.project_name || '—'}
            </MetaItem>
            {editingField === 'start_date' ? (
              <div
                ref={editingBoxRef}
                className="rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 p-3"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-500 mb-1.5 inline-flex items-center gap-2">
                  <CalendarOutlined />
                  تاریخ شروع
                </div>
                <div className="flex items-center gap-1.5">
                  <PersianDatePicker
                    value={fieldValue}
                    onChange={setFieldValue}
                    className="w-full"
                  />
                  <InlineEditActions
                    onSave={() => handleUpdateField('start_date', fieldValue)}
                    onCancel={cancelEditing}
                    saving={updatingField}
                  />
                </div>
              </div>
            ) : (
              <MetaItem
                icon={<CalendarOutlined />}
                label="تاریخ شروع"
                onEdit={() =>
                  startEditing(
                    'start_date',
                    toJalali(task.start_date || task.start_on) || getTodayJalali(),
                  )
                }
              >
                {fmtTaskStart(task)}
              </MetaItem>
            )}
            {editingField === 'due_date' ? (
              <div
                ref={editingBoxRef}
                className="rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 p-3"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-500 mb-1.5 inline-flex items-center gap-2">
                  <CalendarOutlined />
                  سررسید
                </div>
                <div className="flex items-center gap-1.5">
                  <PersianDatePicker
                    value={fieldValue}
                    onChange={setFieldValue}
                    className="w-full"
                  />
                  <InlineEditActions
                    onSave={() => handleUpdateField('due_date', fieldValue)}
                    onCancel={cancelEditing}
                    saving={updatingField}
                  />
                </div>
              </div>
            ) : (
              <MetaItem
                icon={<CalendarOutlined />}
                label="سررسید"
                onEdit={() =>
                  startEditing(
                    'due_date',
                    toJalali(task.due_date || task.due_on) || getTodayJalali(),
                  )
                }
              >
                {fmtTaskDue(task)}
              </MetaItem>
            )}
            <MetaItem icon={<FlagOutlined />} label="دسته">
              {task.category?.category_name || task.task_category?.category_name || '—'}
            </MetaItem>
            {editingField === 'user_id' ? (
              <div
                ref={editingBoxRef}
                className="rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 p-3 sm:col-span-2 lg:col-span-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs text-gray-500 mb-1.5 inline-flex items-center gap-2">
                  <UserOutlined />
                  مسئول
                </div>
                <div className="flex items-center gap-1.5">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={fieldValue}
                    onChange={setFieldValue}
                    options={employeeOptions}
                    style={{ flex: 1, fontFamily: 'CustomFont' }}
                    placeholder="انتخاب مسئول"
                  />
                  <InlineEditActions
                    onSave={() => handleUpdateField('user_id', fieldValue)}
                    onCancel={cancelEditing}
                    saving={updatingField}
                  />
                </div>
              </div>
            ) : (
              <MetaItem
                icon={<UserOutlined />}
                label="مسئولین"
                onEdit={() => startEditing('user_id', primaryUserId)}
              >
                {users.length ? users.map((u) => u.name).join('، ') : '—'}
              </MetaItem>
            )}
          </div>

          {task.description ? (
            <div className="px-5 pb-5">
              <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-4">
                <div className="text-xs text-gray-500 mb-2">توضیحات</div>
                <div className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap leading-7">
                  {task.description}
                </div>
              </div>
            </div>
          ) : null}
        </Card>

        {hasCompanyFeature(user?.company_id ?? task?.company_id, 'task.postalInfoBox') ? (
          <ChaproomPostalInfoBox task={task} />
        ) : null}

        <Card className="shadow-sm border-0">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane={false}
            items={tabItems}
            style={{ fontFamily: 'CustomFont' }}
          />
        </Card>
      </div>

      <Modal
        open={Boolean(editingNote)}
        title={<span style={{ fontFamily: 'CustomFont' }}>ویرایش یادداشت</span>}
        onCancel={() => {
          setEditingNote(null);
          setEditNoteText('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setEditingNote(null);
              setEditNoteText('');
            }}
          >
            انصراف
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={PANEL_PRIMARY_BTN_CLASS}
            loading={editNoteSaving}
            onClick={saveEditNote}
          >
            ذخیره
          </Button>,
        ]}
        destroyOnClose
      >
        <Input.TextArea
          rows={4}
          value={editNoteText}
          onChange={(e) => setEditNoteText(e.target.value)}
          style={{ fontFamily: 'CustomFont' }}
        />
      </Modal>

      <Modal
        open={subModalOpen}
        title={<span style={{ fontFamily: 'CustomFont' }}>زیروظیفه جدید</span>}
        onCancel={() => setSubModalOpen(false)}
        onOk={async () => {
          const v = await subForm.validateFields();
          setSubSaving(true);
          try {
            await subTasksApi.create(buildSubTaskPayload(v, id));
            message.success('زیروظیفه ایجاد شد');
            setSubModalOpen(false);
            setActiveTab('subtasks');
            await refreshTask();
            setActiveTab('subtasks');
          } catch {
            message.error('خطا در ذخیره');
          } finally {
            setSubSaving(false);
          }
        }}
        confirmLoading={subSaving}
        destroyOnClose
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={subForm} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
          <Form.Item name="title" label="عنوان" rules={[{ required: true, message: 'عنوان الزامی است' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="user_id" label="مسئول">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="انتخاب مسئول"
              options={subEmployees.map((e) => ({
                value: e.id,
                label: e.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label="توضیحات">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="start_date" label="تاریخ شروع">
            <PersianDatePicker className="w-full" />
          </Form.Item>
          <Form.Item name="due_date" label="سررسید">
            <PersianDatePicker className="w-full" />
          </Form.Item>
        </Form>
      </Modal>

      <ChaproomPackagingShippingModal
        open={packagingModalOpen}
        confirmLoading={packagingSaving || statusSaving}
        onCancel={handlePackagingModalCancel}
        onSubmit={handlePackagingModalSubmit}
      />
    </WorkDetailPage>
  );
};

export default ViewTask;
